import { Link } from "@tanstack/react-router";
import { Phone, ArrowRight, Check, ChevronRight, ExternalLink } from "lucide-react";
import type { ServiceContent } from "../data/services";
import { SERVICES } from "../data/services";
import { openQuoteModal } from "./quote-modal";
import { QuickLeadForm, serviceValueForSlug } from "./quick-lead-form";
import { TopQuoteForm } from "./top-quote-form";
import { Reveal } from "./reveal";
import { ServiceProofSection, ServiceComparisonSection } from "./service-proof";
import { HouseWashVideoSection, AllServicesVideoSection } from "./service-video";
import { StampedGallerySection } from "./stamped-gallery";
import { PaverLookOptions } from "./paver-look-options";
import { PaverServiceLevels } from "./paver-service-levels";
import { GalleryVideoSlider } from "./gallery-video-slider";
import { FinancingStrip } from "./financing-strip";
import { BlogRelatedStrip } from "./blog-related";
import { siblingSlugsForService } from "../data/service-siblings";
import { GuideStrip } from "./guide-strip";
import { RaindropSection } from "./raindrop-section";
import { HomeReviews } from "./home-reviews";
import { getCityServicesForParent } from "../data/city-services";
import concreteFinished from "../assets/concrete-job/concrete-cleaning-finished.webp.asset.json";
import stampedFinished from "../assets/concrete-job/stamped-concrete-finished.webp.asset.json";
import concreteCrew from "../assets/concrete-job/crew.webp.asset.json";

const PHONE_DISPLAY = "(518) 900-1913";
const PHONE_TEL = "tel:+15189001913";

export function ServicePage({ service }: { service: ServiceContent }) {
  const Icon = service.icon;
  // On commercial pages, surface the sibling commercial sub-services first so
  // the B2B cluster links to itself; residential pages keep the core lineup.
  const isCommercial = service.slug.startsWith("commercial-");
  const pool = SERVICES.filter((s) => s.slug !== service.slug);
  const siblings = siblingSlugsForService(service.slug);
  const residentialPool = pool.filter(
    (s) => !s.slug.startsWith("commercial-") || s.slug === "commercial-pressure-washing",
  );
  const others = (
    isCommercial
      ? [
          ...pool.filter((s) => s.slug.startsWith("commercial-")),
          ...pool.filter((s) => !s.slug.startsWith("commercial-")),
        ]
      : [
          // Topically-closest siblings first, then the rest of the lineup.
          ...siblings
            .map((sl) => residentialPool.find((s) => s.slug === sl))
            .filter((s): s is (typeof residentialPool)[number] => Boolean(s)),
          ...residentialPool.filter((s) => !siblings.includes(s.slug)),
        ]
  ).slice(0, 6);

  // City landing pages owned by this service hub, for a tight internal-link cluster.
  const serviceCities = getCityServicesForParent(service.slug, service.name);

  return (
    <main>
      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="bg-[var(--brand-light-gray)] border-b border-gray-200"
      >
        <div className="mx-auto max-w-[1200px] px-4 py-3 text-sm text-[var(--brand-charcoal)]/70 flex items-center gap-2">
          <Link to="/" className="hover:text-[var(--brand-deep-blue)]">
            Home
          </Link>
          <ChevronRight size={14} />
          <span className="text-[var(--brand-deep-blue)] font-semibold">
            {service.name}
          </span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative bg-[var(--brand-deep-blue)] text-white overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #1E6FD9 0, transparent 45%), radial-gradient(circle at 80% 70%, #C8102E 0, transparent 45%)",
          }}
        />
        <div className="relative mx-auto max-w-[1200px] px-4 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1 rounded-full mb-4">
              <Icon className="text-[#C8102E]" size={18} />
              <span
                className="text-xs font-bold uppercase tracking-[0.18em] text-white/90"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {service.hero.eyebrow}
              </span>
            </div>
            <h1
              className="text-4xl md:text-6xl uppercase leading-[1.05] tracking-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              {service.hero.headline}
            </h1>
            <p className="mt-5 text-lg md:text-xl text-white/85 leading-relaxed max-w-xl">
              {service.hero.subhead}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {service.externalUrl ? (
                <a
                  href={service.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pop btn-shimmer inline-flex items-center justify-center gap-2 bg-[var(--brand-red)] hover:bg-[#a00d25] text-white font-bold uppercase tracking-wide px-6 py-4 rounded-md text-base"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {service.externalCta || "Learn More"} <ExternalLink size={18} />
                </a>
              ) : (
                <button
                  onClick={() => openQuoteModal()}
                  className="btn-pop btn-shimmer inline-flex items-center justify-center gap-2 bg-[var(--brand-red)] hover:bg-[#a00d25] text-white font-bold uppercase tracking-wide px-6 py-4 rounded-md text-base"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
              )}
              <a
                href={PHONE_TEL}
                className="btn-pop inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wide px-6 py-4 rounded-md text-base border border-white/30"
                style={{ fontFamily: "var(--font-display)" }}
              >
                <Phone size={18} /> {PHONE_DISPLAY}
              </a>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
              <li className="flex items-center gap-2">
                <Check size={16} className="text-[#C8102E]" /> 10+ Years Experience
              </li>
              <li className="flex items-center gap-2">
                <Check size={16} className="text-[#C8102E]" /> Fast Free Quotes
              </li>
              <li className="flex items-center gap-2">
                <Check size={16} className="text-[#C8102E]" /> Local Capital Region Crew
              </li>
            </ul>
          </div>

          {/* Service hero image */}
          <div className="relative">
            {service.slug === "concrete-cleaning-sealing" ? (
              <div className="grid aspect-[4/3] grid-cols-2 gap-1 overflow-hidden rounded-lg bg-white/5 shadow-2xl ring-1 ring-white/10">
                <img
                  src={concreteFinished.url}
                  alt="Professionally cleaned standard concrete patio by Capital Pro Pressure Washing"
                  className="h-full min-w-0 object-cover"
                  loading="eager"
                  width={600}
                  height={800}
                />
                <img
                  src={stampedFinished.url}
                  alt="Dark gray stamped concrete patio cleaned and sealed by Capital Pro Pressure Washing"
                  className="h-full min-w-0 object-cover"
                  loading="eager"
                  width={600}
                  height={800}
                />
              </div>
            ) : (
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10 bg-white/5">
              {service.heroVideo ? (
                <video
                  src={service.heroVideo}
                  poster={service.heroImage}
                  className="h-full w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={`${service.name} by Capital Pro Pressure Washing`}
                />
              ) : (
                <img
                  src={service.heroImage}
                  alt={`${service.name} by Capital Pro Pressure Washing`}
                  className="h-full w-full object-cover"
                  loading="eager"
                  {...(service.heroPlaceholder ? { "data-placeholder": service.heroPlaceholder } : {})}
                />
              )}
              </div>
            )}
            {service.slug === "concrete-cleaning-sealing" && (
              <div className="absolute -top-6 right-3 w-24 overflow-hidden rounded-md border-4 border-white bg-white shadow-xl sm:-right-5 sm:w-32">
                <img
                  src={concreteCrew.url}
                  alt="Capital Pro crew on a completed stamped concrete project"
                  className="aspect-[3/4] w-full object-cover object-center"
                  loading="eager"
                  width={288}
                  height={384}
                />
                <p className="px-1.5 py-1 text-center text-[9px] font-bold uppercase leading-tight text-[var(--brand-deep-blue)] sm:text-[10px]">
                  Your Local Crew
                </p>
              </div>
            )}
            <div className="absolute -bottom-4 -left-4 bg-white text-[var(--brand-deep-blue)] px-4 py-3 rounded-md shadow-lg">
              <p className="text-xs uppercase font-bold tracking-widest text-[var(--brand-red)]">
                Serving
              </p>
              <p className="text-sm font-semibold">Capital Region, NY</p>
            </div>
            {service.heroCaption && (
              <p className="mt-6 text-center text-xs sm:text-sm uppercase tracking-[0.2em] text-white/75 font-semibold">
                {service.heroCaption}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Quick quote form directly under the hero */}
      {!service.externalUrl && (
        <TopQuoteForm
          source={`Service Top — ${service.name}`}
          defaultService={serviceValueForSlug(service.slug)}
          heading={`Free ${service.name} Quote`}
        />
      )}

      {/* Intro */}
      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-[820px] px-4 text-center">
          <p
            className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)" }}
          >
            About This Service
          </p>
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            {service.name} Done Right the First Time
          </h2>
          <p className="mt-5 text-[var(--brand-charcoal)]/85 text-lg leading-relaxed">
            {service.intro}
          </p>
          {service.externalUrl && (
            <div className="mt-8 rounded-xl border border-[var(--brand-deep-blue)]/15 bg-[var(--brand-light-gray)] p-6 text-left">
              <p className="text-sm font-semibold uppercase tracking-wider text-[var(--brand-red)]">
                Dedicated Lighting Site
              </p>
              <p className="mt-2 text-[var(--brand-charcoal)]/80">
                We built a separate site just for permanent lighting — full galleries, pricing, and booking at{" "}
                <a
                  href={service.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-[var(--brand-deep-blue)] hover:text-[var(--brand-red)] underline underline-offset-4"
                >
                  www.capitalprolighting.com
                  <ExternalLink size={14} />
                </a>
                .
              </p>
            </div>
          )}
        </div>
      </section>

      {service.slug === "house-washing" && <HouseWashVideoSection />}
      {service.slug === "roof-washing" && <AllServicesVideoSection />}
      {(service.slug === "roof-washing" || service.slug === "soft-washing") && (
        <div className="bg-white py-6">
          <p className="mx-auto max-w-[820px] px-4 text-center text-[var(--brand-charcoal)]/85">
            Searching for{" "}
            <a
              href={`/pressure-washing-near-me#${service.slug === "roof-washing" ? "roof-washing-near-me" : "soft-washing-near-me"}`}
              className="text-[var(--brand-bright-blue)] underline underline-offset-2 font-semibold"
            >
              {service.slug === "roof-washing" ? "roof washing near me" : "soft washing near me"}
            </a>
            ? We serve Albany, Troy, Schenectady, Clifton Park, Saratoga Springs and the whole Capital Region.
          </p>
        </div>
      )}
      {service.slug === "gutter-guards" && <RaindropSection />}

      {/* Benefits */}
      <section className="bg-[var(--brand-light-gray)] py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4">
          <div className="text-center mb-10 md:mb-12">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Why It Matters
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              What You Get With Capital Pro
            </h2>
          </div>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            style={{ perspective: "1000px" }}
          >
            {service.benefits.map((b, i) => (
              <Reveal
                key={b.title}
                delayMs={i * 80}
                variant="flap"
                className="card-pop bg-white rounded-lg p-6 border border-gray-200 shadow-sm"
              >
                <div className="w-10 h-10 rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)] flex items-center justify-center mb-4">
                  <Check size={20} strokeWidth={3} />
                </div>
                <h3
                  className="text-[var(--brand-deep-blue)] text-lg mb-2 uppercase"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {b.title}
                </h3>
                <p className="text-[var(--brand-charcoal)]/85 text-sm leading-relaxed">
                  {b.desc}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {service.proof && <ServiceProofSection proof={service.proof} />}

      {service.slug === "concrete-cleaning-sealing" && <StampedGallerySection />}

      {service.slug === "paver-cleaning-sealing" && <PaverServiceLevels />}

      {service.slug === "paver-cleaning-sealing" && <PaverLookOptions />}

      {service.slug === "paver-cleaning-sealing" && (
        <section className="bg-[var(--brand-light,#F4F6F8)] py-16 md:py-20">
          <div className="mx-auto max-w-[1200px] px-4">
            <div className="text-center mb-8 md:mb-10">
              <p
                className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
                style={{ fontFamily: "var(--font-display)" }}
              >
                On The Job
              </p>
              <h2
                className="text-3xl md:text-4xl font-bold uppercase text-[var(--brand-blue,#0B3D7A)]"
                style={{ fontFamily: "var(--font-display)" }}
              >
                See Our Crew In Action
              </h2>
            </div>
            <GalleryVideoSlider />
          </div>
        </section>
      )}

      {service.comparison && <ServiceComparisonSection comparison={service.comparison} />}

      {/* City cluster */}
      {serviceCities.length > 0 && (
        <section className="bg-white py-14 md:py-16 border-t border-gray-100">
          <div className="mx-auto max-w-[1100px] px-4">
            <div className="text-center mb-7">
              <p
                className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Where We Work
              </p>
              <h2
                className="text-[var(--brand-deep-blue)] text-2xl md:text-3xl uppercase leading-tight"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                {service.name} By Town
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {serviceCities.map((c) => (
                <a
                  key={c.slug}
                  href={`/${c.slug}`}
                  className="group flex items-center justify-between rounded-md border border-[var(--brand-deep-blue)]/10 bg-[var(--brand-light-gray)] p-4 hover:border-[var(--brand-bright-blue)] hover:shadow-md transition-all"
                >
                  <span className="text-sm font-semibold text-[var(--brand-charcoal)] group-hover:text-[var(--brand-bright-blue)] transition-colors">
                    {c.service} in {c.city}, NY
                  </span>
                  <ChevronRight size={16} className="text-[var(--brand-charcoal)]/40 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Long-form topical sections */}
      {service.sections && service.sections.length > 0 && (
        <section className="bg-[var(--brand-light-gray)] py-16 md:py-20">
          <div className="mx-auto max-w-[820px] px-4 space-y-12">
            {service.sections.map((s) => (
              <div key={s.heading}>
                {s.eyebrow && (
                  <p
                    className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {s.eyebrow}
                  </p>
                )}
                <h2
                  className="text-[var(--brand-deep-blue)] text-2xl md:text-3xl uppercase leading-tight"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {s.heading}
                </h2>
                <div className="mt-4 space-y-4 text-[var(--brand-charcoal)]/85 text-base md:text-lg leading-relaxed">
                  {s.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Process */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4">
          <div className="text-center mb-10 md:mb-12">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Our Process
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Simple, Professional, Guaranteed
            </h2>
          </div>
          <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 ${service.process.length === 4 ? "lg:grid-cols-4" : "md:grid-cols-3"}`}>
            {service.process.map((step, i) => (
              <Reveal
                key={step.title}
                delayMs={i * 100}
                className="relative bg-[var(--brand-light-gray)] rounded-lg p-6 border border-gray-200"
              >
                <div
                  className="absolute -top-4 -left-2 bg-[var(--brand-deep-blue)] text-white w-12 h-12 rounded-full flex items-center justify-center text-xl shadow-md"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {i + 1}
                </div>
                <h3
                  className="mt-3 text-[var(--brand-deep-blue)] text-lg uppercase mb-2"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {step.title}
                </h3>
                <p className="text-[var(--brand-charcoal)]/85 text-sm leading-relaxed">
                  {step.desc}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinancingStrip />

      {/* Mid CTA band */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-12">
        <div className="mx-auto max-w-[1200px] px-4 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h3
              className="text-2xl md:text-3xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              {service.externalUrl
                ? `See More on Capital Pro Lighting`
                : `Ready for a Free ${service.name} Quote?`}
            </h3>
            <p className="mt-2 text-white/80">
              {service.externalUrl
                ? "Full galleries, pricing, and scheduling on our dedicated lighting site."
                : "Fast callback. No pressure, no obligation."}
            </p>
            {!service.externalUrl && (
              <p className="mt-3">
                <span
                  className="inline-flex items-center rounded-full bg-[var(--brand-red)] px-3.5 py-1.5 text-white text-xs uppercase tracking-[0.12em]"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  Save 10% when you book 2+ services
                </span>
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {service.externalUrl ? (
              <a
                href={service.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pop btn-shimmer inline-flex items-center justify-center gap-2 bg-[var(--brand-red)] hover:bg-[#a00d25] text-white font-bold uppercase tracking-wide px-6 py-3 rounded-md"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {service.externalCta || "Learn More"} <ExternalLink size={18} />
              </a>
            ) : (
              <button
                onClick={() => openQuoteModal()}
                className="btn-pop btn-shimmer inline-flex items-center justify-center gap-2 bg-[var(--brand-red)] hover:bg-[#a00d25] text-white font-bold uppercase tracking-wide px-6 py-3 rounded-md"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Get Free Quote <ArrowRight size={18} />
              </button>
            )}
            <a
              href={PHONE_TEL}
              className="btn-pop inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wide px-6 py-3 rounded-md border border-white/30"
              style={{ fontFamily: "var(--font-display)" }}
            >
              <Phone size={18} /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[820px] px-4">
          <div className="text-center mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
              style={{ fontFamily: "var(--font-display)" }}
            >
              FAQ
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {service.faqs.map((f) => (
              <details
                key={f.q}
                className="group bg-[var(--brand-light-gray)] rounded-lg border border-gray-200 open:shadow-sm"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between p-5 font-semibold text-[var(--brand-deep-blue)]">
                  <span>{f.q}</span>
                  <ChevronRight
                    size={20}
                    className="transition-transform group-open:rotate-90 text-[var(--brand-red)]"
                  />
                </summary>
                <div className="px-5 pb-5 text-[var(--brand-charcoal)]/85 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Inline Quote Form — or external-site CTA for linked services */}
      <section
        id="quote"
        aria-labelledby="service-quote-title"
        className="bg-[var(--brand-light-gray)] py-16 md:py-20 scroll-mt-24"
      >
        <div className="mx-auto max-w-[640px] px-4">
          <div className="text-center mb-8">
            <p
              className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--brand-red)] mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Get Started
            </p>
            <h2
              id="service-quote-title"
              className="text-3xl md:text-4xl font-bold uppercase text-[var(--brand-deep-blue)] tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {service.externalUrl
                ? "Get a Quote on Capital Pro Lighting"
                : `Get Your Free ${service.name} Quote`}
            </h2>
            <p className="mt-3 text-[var(--brand-charcoal)]/80 text-base">
              {service.externalUrl
                ? "Pricing, galleries, and booking are handled on our dedicated lighting site."
                : "Takes about a minute. Fast callback in the Capital Region."}
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
            {service.externalUrl ? (
              <div className="text-center">
                <p className="mb-6 text-[var(--brand-charcoal)]/80">
                  For permanent lighting quotes, scheduling, and full project galleries, visit our dedicated site.
                </p>
                <a
                  href={service.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pop btn-shimmer inline-flex items-center justify-center gap-2 bg-[var(--brand-red)] hover:bg-[#a00d25] text-white font-bold uppercase tracking-wide px-6 py-4 rounded-md text-base"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {service.externalCta || "Learn More"} <ExternalLink size={18} />
                </a>
                <p className="mt-4 text-sm text-[var(--brand-charcoal)]/60">
                  Or call us at{" "}
                  <a href={PHONE_TEL} className="font-semibold text-[var(--brand-deep-blue)]">
                    {PHONE_DISPLAY}
                  </a>
                </p>
              </div>
            ) : (
              <QuickLeadForm
                showHeader={false}
                source={`Service Bottom — ${service.name}`}
                defaultService={serviceValueForSlug(service.slug)}
              />
            )}
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="bg-white py-16 border-t border-gray-100">
        <div className="mx-auto max-w-[1200px] px-4">
          <div className="text-center mb-8">
            <h2
              className="text-[var(--brand-deep-blue)] text-2xl md:text-3xl uppercase"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Explore Our Other Services
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {others.map((s) => {
              const OI = s.icon;
              return (
                <Link
                  key={s.slug}
                  to="/$service"
                  params={{ service: s.slug }}
                  className="group flex flex-col items-center text-center gap-2 p-4 rounded-lg border border-gray-200 hover:border-[var(--brand-red)] hover:shadow-md transition"
                >
                  <OI className="text-[var(--brand-red)] group-hover:scale-110 transition" size={28} />
                  <span className="text-sm font-semibold text-[var(--brand-deep-blue)] leading-tight">
                    {s.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <GuideStrip serviceSlug={service.slug} serviceName={service.name} />
      <HomeReviews />
      <BlogRelatedStrip topic={service.name} />
    </main>
  );
}